import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { foyerApi, type PlaybackSurface } from "../http/foyer";
import type { Translate } from "../i18n/translate";
import { errorText } from "../utils/apiError";
import { tokens } from "../theme";
import {
  BarList,
  Heatmap,
  KpiTile,
  TrendLegend,
  TrendLine,
  type Better,
} from "../components/playbackAnalytics/charts";
import {
  BreakdownTable,
  type MetricColumn,
} from "../components/playbackAnalytics/BreakdownTable";
import {
  AdDeliveryView,
  VistarCatalogueBar,
} from "../components/playbackAnalytics/AdDelivery";
import {
  usePlaybackFormat,
  type PlaybackFormat,
} from "../components/playbackAnalytics/format";
import type {
  PlaybackBreakdown,
  PlaybackBreakdownRow,
  PlaybackDimension,
  PlaybackFilterOptions,
  PlaybackFilters,
  PlaybackHeatmap,
  PlaybackMetrics,
  PlaybackSeriesPoint,
  PlaybackSummary,
  SignageBreakdown,
  AdDeliveryReport,
  VistarCatalogueStatus,
} from "@shared/types/playbackAnalytics";

/**
 * Analytics → Content analytics: what played on the TVs, how much of it was
 * watched, where (which premise), whose (which provider) and how well it
 * played.
 *
 * Data: the vendor TV app's own playback reports, folded into sessions in
 * ClickHouse and read live (`src/shared/types/playbackAnalytics.ts` has the
 * metric definitions). Every filter lives in the URL, so a view is a link
 * someone can paste into an email.
 *
 * Drilling in IS filtering: clicking a title narrows every tab to that title,
 * a provider to its titles, a premise to that premise. The focus bar above
 * the tabs says what is narrowed and clears it.
 */

type TabKey =
  | "overview"
  | "content"
  | "providers"
  | "premises"
  | "quality"
  | "ads";
type TrendMetric = "plays" | "watchHours" | "uniqueRooms";
type QualityMetric = "startupMedianMs" | "rebufferRatio" | "failureRate";

const RANGES = [
  "today",
  "yesterday",
  "7",
  "30",
  "90",
  "365",
  "custom",
] as const;

const SEPARATOR = " · ";

const FILTER_KEYS = [
  "chain",
  "kind",
  "provider",
  "collection",
  "asset",
  "device",
] as const;

/**
 * Staff, or a content owner. The owner surface is the same page over the
 * owner API, which the SERVER narrows to the owner's titles + its portal's
 * catalogue and strips of premise names and quality. The page only hides
 * what the server would refuse anyway.
 */
const SurfaceCtx = createContext<PlaybackSurface>("staff");
const useSurface = () => useContext(SurfaceCtx);
const OWNER_TABS = new Set<TabKey>(["overview", "content", "providers"]);

// Staff only (never in OWNER_TABS): "ads", proof of play for advertising.
const TABS: TabKey[] = [
  "overview",
  "content",
  "providers",
  "premises",
  "quality",
  "ads",
];

/** Today's date in Swedish time, as `YYYY-MM-DD`: a key, not display text. */
function today(): string {
  const parts = new Intl.DateTimeFormat(undefined, {
    timeZone: "Europe/Stockholm",
    numberingSystem: "latn",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function shiftDay(day: string, days: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function windowOf(range: string, params: URLSearchParams) {
  const t = today();
  switch (range) {
    case "today":
      return { from: t, to: t };
    case "yesterday":
      return { from: shiftDay(t, -1), to: shiftDay(t, -1) };
    case "custom": {
      const to = params.get("to") || t;
      return { from: params.get("from") || shiftDay(to, -29), to };
    }
    default: {
      const n = Number(range) || 30;
      return { from: shiftDay(t, -(n - 1)), to: t };
    }
  }
}

/**
 * A comparison is only honest when the previous period lies inside the data
 * we have: before collection started there is nothing, and "new" on every
 * number would be noise, not news.
 */
function comparable(s: PlaybackSummary): boolean {
  return Boolean(s.dataSince && s.previousRange.from >= s.dataSince);
}

/** A failed load, in the operator's words. */
function loadErrorText(t: Translate, err: unknown): string {
  const { status } = (err ?? {}) as { status?: number };
  if (status === 403) return t("analytics:contentAnalyticsPage.forbidden");
  return errorText(err, t);
}

/** Load something for the current inputs; drops a stale response. */
function useLoad<T>(load: () => Promise<T>, key: string) {
  const { t } = useTranslation("analytics");
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(loadErrorText(t, err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `key` is the full identity of the request; `load` closes over it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { data, error, loading };
}

/* ------------------------------------------------------------------------ */
/* Quality thresholds                                                        */
/* ------------------------------------------------------------------------ */

type Level = "good" | "warning" | "critical";
type QualityKey =
  | "startupMedianMs"
  | "rebufferRatio"
  | "playbackFailureRate"
  | "startFailureRate"
  | "ebvsRate";

/**
 * Industry guidance (Mux / Conviva): startup under 2 s is fine and abandonment
 * climbs past 4 s; rebuffering above ~1 % of viewing is felt; failure rates
 * above a few percent need attention. Thresholds, not targets: a care home on
 * a thin uplink will sit differently from a city hotel.
 */
const BANDS: Record<QualityKey, [number, number]> = {
  startupMedianMs: [2000, 4000],
  rebufferRatio: [0.005, 0.02],
  playbackFailureRate: [0.01, 0.03],
  startFailureRate: [0.01, 0.03],
  ebvsRate: [0.05, 0.1],
};

export function qualityLevel(
  key: QualityKey,
  v: number | null
): Level | undefined {
  if (v === null) return undefined;
  const [ok, bad] = BANDS[key];
  if (v <= ok) return "good";
  if (v <= bad) return "warning";
  return "critical";
}

function useQualityStatus() {
  const { t } = useTranslation("analytics");
  return (key: QualityKey, v: number | null) => {
    const level = qualityLevel(key, v);
    return level
      ? { level, text: t(`contentAnalyticsPage.qualityLevel.${level}`) }
      : undefined;
  };
}

/* ------------------------------------------------------------------------ */
/* Columns                                                                   */
/* ------------------------------------------------------------------------ */

type ColumnKey =
  | "plays"
  | "rooms"
  | "premises"
  | "watch"
  | "avg"
  | "completion"
  | "attempts"
  | "startup"
  | "startupP90"
  | "ebvs"
  | "startFail"
  | "playFail"
  | "rebuffer"
  | "rebuffersH"
  | "bitrate";

const COL_DEFS: Record<
  ColumnKey,
  {
    key: keyof PlaybackMetrics;
    label:
      | "plays"
      | "units"
      | "premises"
      | "watchTime"
      | "avgPerPlay"
      | "completed"
      | "attempts"
      | "startup"
      | "startupP90"
      | "gaveUp"
      | "startFailed"
      | "failedInPlay"
      | "buffering"
      | "stallsPerHour"
      | "bitrate";
    format: keyof PlaybackFormat;
    better?: Better;
  }
> = {
  plays: { key: "plays", label: "plays", format: "count", better: "up" },
  rooms: { key: "uniqueRooms", label: "units", format: "count", better: "up" },
  premises: {
    key: "premises",
    label: "premises",
    format: "count",
    better: "up",
  },
  watch: {
    key: "watchHours",
    label: "watchTime",
    format: "hours",
    better: "up",
  },
  avg: {
    key: "avgViewMinutes",
    label: "avgPerPlay",
    format: "minutes",
    better: "up",
  },
  completion: {
    key: "completionRate",
    label: "completed",
    format: "percent",
    better: "up",
  },
  attempts: { key: "attempts", label: "attempts", format: "count" },
  startup: {
    key: "startupMedianMs",
    label: "startup",
    format: "ms",
    better: "down",
  },
  startupP90: {
    key: "startupP90Ms",
    label: "startupP90",
    format: "ms",
    better: "down",
  },
  ebvs: { key: "ebvsRate", label: "gaveUp", format: "percent", better: "down" },
  startFail: {
    key: "startFailureRate",
    label: "startFailed",
    format: "percent",
    better: "down",
  },
  playFail: {
    key: "playbackFailureRate",
    label: "failedInPlay",
    format: "percent",
    better: "down",
  },
  rebuffer: {
    key: "rebufferRatio",
    label: "buffering",
    format: "percent",
    better: "down",
  },
  rebuffersH: {
    key: "rebuffersPerHour",
    label: "stallsPerHour",
    format: "perHour",
    better: "down",
  },
  bitrate: { key: "avgBitrateKbps", label: "bitrate", format: "kbps" },
};

const QUALITY_COLUMNS: ColumnKey[] = [
  "attempts",
  "plays",
  "startup",
  "startupP90",
  "ebvs",
  "startFail",
  "playFail",
  "rebuffer",
  "rebuffersH",
  "bitrate",
];

/** The metric columns, labelled and formatted in the CMS language. */
function useColumns(): Record<ColumnKey, MetricColumn> {
  const { t } = useTranslation("analytics");
  const pf = usePlaybackFormat();
  return useMemo(() => {
    const out = {} as Record<ColumnKey, MetricColumn>;
    for (const [name, d] of Object.entries(COL_DEFS)) {
      const format = pf[d.format] as (v: number | null) => string;
      out[name as ColumnKey] = {
        key: d.key,
        label: t(`contentAnalyticsPage.columns.${d.label}`),
        format,
        better: d.better,
      };
    }
    return out;
  }, [t, pf]);
}

/* ------------------------------------------------------------------------ */
/* The page                                                                  */
/* ------------------------------------------------------------------------ */

export default function ContentAnalyticsPage({
  surface = "staff",
}: {
  surface?: PlaybackSurface;
}) {
  return (
    <SurfaceCtx.Provider value={surface}>
      <PlaybackAnalytics />
    </SurfaceCtx.Provider>
  );
}

function PlaybackAnalytics() {
  const { t } = useTranslation("analytics");
  const pf = usePlaybackFormat();
  const surface = useSurface();
  const owner = surface === "owner";
  const [params, setParams] = useSearchParams();

  const range = params.get("range") ?? "30";
  const tab = (params.get("tab") as TabKey) || "overview";
  const compare = params.get("compare") !== "0";
  const { from, to } = windowOf(range, params);
  const filters: PlaybackFilters = useMemo(() => {
    const f: PlaybackFilters = { from, to };
    for (const k of [
      "chain",
      "premise",
      "asset",
      "provider",
      "collection",
      "device",
    ] as const) {
      const v = params.get(k);
      if (v) f[k] = v;
    }
    const kind = params.get("kind");
    if (kind === "live" || kind === "vod") f.kind = kind;
    return f;
  }, [from, to, params]);
  const filterKey = JSON.stringify(filters);

  const setParam = useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(params);
      for (const [k, v] of Object.entries(updates)) {
        if (v) next.set(k, v);
        else next.delete(k);
      }
      setParams(next);
    },
    [params, setParams]
  );

  const options = useLoad<PlaybackFilterOptions>(
    () => foyerApi.playbackOptions({ from, to }, surface),
    `${from}|${to}`
  );
  const summary = useLoad<PlaybackSummary>(
    () => foyerApi.playbackSummary(filters, surface),
    filterKey
  );

  // Names for the focus bar, learned from whichever breakdown showed them.
  const [labels, setLabels] = useState<Record<string, string>>({});
  const remember = useCallback(
    (k: string, v: string) =>
      setLabels((prev) => (prev[k] === v ? prev : { ...prev, [k]: v })),
    []
  );

  const drill = (updates: Record<string, string | null>, nextTab?: TabKey) =>
    setParam({
      ...updates,
      ...(nextTab ? { tab: nextTab === "overview" ? null : nextTab } : {}),
    });

  const exportDim: PlaybackDimension =
    tab === "providers"
      ? "provider"
      : tab === "premises"
        ? "premise"
        : tab === "quality"
          ? "device"
          : "asset";

  // The Ads tab stands on its own: Foyer's ad ledger is Postgres, so it must
  // render where the analytics database is missing or down, and none of the
  // playback summary's warnings are about it.
  const adsTab = tab === "ads" && !owner;
  const error = adsTab ? null : summary.error;

  return (
    <PageContainer>
      <PageHeader
        eyebrow={
          owner
            ? t("contentAnalyticsPage.eyebrowOwner")
            : t("contentAnalyticsPage.eyebrowStaff")
        }
        title={
          owner
            ? t("contentAnalyticsPage.titleOwner")
            : t("contentAnalyticsPage.titleStaff")
        }
        actions={
          <Button
            component="a"
            href={
              adsTab
                ? foyerApi.adDeliveryCsvUrl(filters)
                : foyerApi.playbackBreakdownCsvUrl(filters, exportDim, surface)
            }
            startIcon={<DownloadOutlinedIcon />}
            variant="outlined"
            data-testid="playbacks-export"
          >
            {t("contentAnalyticsPage.exportCsv")}
          </Button>
        }
      />

      <FilterBar
        range={range}
        from={from}
        to={to}
        filters={filters}
        compare={compare}
        options={options.data}
        optionsLoading={options.loading}
        optionsError={options.error}
        setParam={setParam}
      />

      <FocusBar
        filters={filters}
        labels={labels}
        options={options.data}
        clear={(k) => setParam({ [k]: null })}
      />

      {!adsTab &&
        summary.data?.collection &&
        !summary.data.collection.found && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            data-testid="playbacks-collection-missing"
          >
            {t("contentAnalyticsPage.collectionMissing", {
              id: summary.data.collection.id,
            })}
          </Alert>
        )}
      {!adsTab && summary.data?.collection?.truncated && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="playbacks-collection-truncated"
        >
          {t("contentAnalyticsPage.collectionTruncated", {
            name: summary.data.collection.name,
            count: pf.count(summary.data.collection.assetCount),
          })}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="playbacks-error">
          {error}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v: TabKey) =>
          setParam({ tab: v === "overview" ? null : v })
        }
        sx={{ mb: 2, borderBottom: `1px solid ${tokens.line}` }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        {TABS.filter((key) => !owner || OWNER_TABS.has(key)).map((key) => (
          <Tab
            key={key}
            value={key}
            label={t(`contentAnalyticsPage.tabs.${key}`)}
            data-testid={`playbacks-tab-${key}`}
          />
        ))}
      </Tabs>

      {adsTab && <AdsTab filters={filters} compare={compare} />}

      {!adsTab && !error && !summary.data && summary.loading && (
        <CircularProgress size={24} />
      )}

      {!adsTab &&
        summary.data &&
        !error &&
        compare &&
        !comparable(summary.data) && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            data-testid="playbacks-no-compare"
          >
            {t("contentAnalyticsPage.noCompare", {
              since: summary.data.dataSince
                ? pf.day(summary.data.dataSince)
                : t("contentAnalyticsPage.recently"),
              from: summary.data.previousRange.from,
              to: summary.data.previousRange.to,
            })}
          </Alert>
        )}

      {!adsTab && summary.data && !error && (
        <Box
          sx={{ opacity: summary.loading ? 0.6 : 1, transition: "opacity .2s" }}
        >
          {tab === "overview" && (
            <Overview
              summary={summary.data}
              filters={filters}
              compare={compare && comparable(summary.data)}
              drill={drill}
              remember={remember}
            />
          )}
          {tab === "content" && (
            <ContentTab
              filters={filters}
              compare={compare && comparable(summary.data)}
              drill={drill}
              remember={remember}
            />
          )}
          {tab === "providers" && (
            <ProvidersTab
              filters={filters}
              compare={compare && comparable(summary.data)}
              drill={drill}
              remember={remember}
            />
          )}
          {tab === "premises" && (
            <PremisesTab
              filters={filters}
              compare={compare && comparable(summary.data)}
              drill={drill}
              remember={remember}
            />
          )}
          {tab === "quality" && (
            <QualityTab
              summary={summary.data}
              filters={filters}
              compare={compare && comparable(summary.data)}
              drill={drill}
              remember={remember}
            />
          )}
          <Footnote summary={summary.data} />
        </Box>
      )}
    </PageContainer>
  );
}

/* ------------------------------------------------------------------------ */
/* Filter bar and focus bar                                                  */
/* ------------------------------------------------------------------------ */

function FilterBar({
  range,
  from,
  to,
  filters,
  compare,
  options,
  optionsLoading,
  optionsError,
  setParam,
}: {
  range: string;
  from: string;
  to: string;
  filters: PlaybackFilters;
  compare: boolean;
  options: PlaybackFilterOptions | null;
  optionsLoading: boolean;
  optionsError: string | null;
  setParam: (u: Record<string, string | null>) => void;
}) {
  const { t } = useTranslation("analytics");
  const owner = useSurface() === "owner";
  const premiseOptions = options?.premises ?? [];
  const providerOptions = options?.providers ?? [];
  const collectionOptions = options?.collections ?? [];
  // While the options are on their way (after a restart the title lookup
  // behind them can take ~40 s) the lists say so, and a failed load says
  // that - never a bare "No options", which reads as "there are none".
  const pending = !options && optionsLoading;
  const listState = {
    loading: pending,
    loadingText: t("contentAnalyticsPage.filters.loading"),
    noOptionsText:
      !options && optionsError
        ? t("contentAnalyticsPage.filters.listFailed")
        : t("contentAnalyticsPage.filters.noMatches"),
  };
  return (
    <Stack
      direction="row"
      spacing={1.5}
      flexWrap="wrap"
      useFlexGap
      sx={{ mb: 2 }}
    >
      <TextField
        select
        size="small"
        label={t("contentAnalyticsPage.filters.period")}
        value={range}
        onChange={(e) =>
          setParam(
            e.target.value === "custom"
              ? { range: "custom", from, to }
              : {
                  range: e.target.value === "30" ? null : e.target.value,
                  from: null,
                  to: null,
                }
          )
        }
        sx={{ minWidth: 160 }}
        data-testid="playbacks-range"
      >
        {RANGES.map((r) => (
          <MenuItem key={r} value={r}>
            {t(`contentAnalyticsPage.ranges.${r}`)}
          </MenuItem>
        ))}
      </TextField>
      {range === "custom" && (
        <>
          <TextField
            size="small"
            type="date"
            label={t("contentAnalyticsPage.filters.from")}
            value={from}
            onChange={(e) => setParam({ from: e.target.value })}
            InputLabelProps={{ shrink: true }}
            data-testid="playbacks-from"
          />
          <TextField
            size="small"
            type="date"
            label={t("contentAnalyticsPage.filters.to")}
            value={to}
            onChange={(e) => setParam({ to: e.target.value })}
            InputLabelProps={{ shrink: true }}
            data-testid="playbacks-to"
          />
        </>
      )}
      <TextField
        select
        size="small"
        label={t("contentAnalyticsPage.filters.chain")}
        value={filters.chain ?? ""}
        onChange={(e) => setParam({ chain: e.target.value })}
        sx={{ minWidth: 140 }}
        data-testid="playbacks-chain"
      >
        <MenuItem value="">
          {t("contentAnalyticsPage.filters.allChains")}
        </MenuItem>
        {(options?.chains ?? (filters.chain ? [filters.chain] : [])).map(
          (c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          )
        )}
      </TextField>
      <TextField
        select
        size="small"
        label={t("contentAnalyticsPage.filters.type")}
        value={filters.kind ?? ""}
        onChange={(e) => setParam({ kind: e.target.value })}
        sx={{ minWidth: 150 }}
        data-testid="playbacks-kind"
      >
        <MenuItem value="">
          {t("contentAnalyticsPage.filters.liveAndVod")}
        </MenuItem>
        <MenuItem value="vod">{t("contentAnalyticsPage.filters.vod")}</MenuItem>
        <MenuItem value="live">
          {t("contentAnalyticsPage.filters.liveChannels")}
        </MenuItem>
      </TextField>
      <Autocomplete
        size="small"
        options={providerOptions}
        getOptionLabel={(o) => o.label}
        value={providerOptions.find((o) => o.id === filters.provider) ?? null}
        onChange={(_, v) => setParam({ provider: v?.id ?? null })}
        renderInput={(p) => (
          <TextField
            {...p}
            label={t("contentAnalyticsPage.filters.provider")}
          />
        )}
        sx={{ minWidth: 200 }}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        {...listState}
        data-testid="playbacks-provider"
      />
      {!owner && (
        <Autocomplete
          size="small"
          options={collectionOptions}
          getOptionLabel={(o) => o.label}
          value={
            collectionOptions.find((o) => o.id === filters.collection) ?? null
          }
          onChange={(_, v) => setParam({ collection: v?.id ?? null })}
          renderInput={(p) => (
            <TextField
              {...p}
              label={t("contentAnalyticsPage.filters.collection")}
            />
          )}
          sx={{ minWidth: 220 }}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          {...listState}
          data-testid="playbacks-collection"
        />
      )}
      {!owner && (
        <Autocomplete
          size="small"
          options={premiseOptions}
          getOptionLabel={(o) => o.label}
          value={premiseOptions.find((o) => o.key === filters.premise) ?? null}
          onChange={(_, v) => setParam({ premise: v?.key ?? null })}
          renderInput={(p) => (
            <TextField
              {...p}
              label={t("contentAnalyticsPage.filters.premise")}
            />
          )}
          sx={{ minWidth: 240 }}
          isOptionEqualToValue={(a, b) => a.key === b.key}
          {...listState}
          data-testid="playbacks-premise"
        />
      )}
      <FormControlLabel
        control={
          <Switch
            checked={compare}
            onChange={(e) =>
              setParam({ compare: e.target.checked ? null : "0" })
            }
            inputProps={{
              "aria-label": t("contentAnalyticsPage.filters.compare"),
            }}
            data-testid="playbacks-compare"
          />
        }
        label={
          <Typography variant="bodySm">
            {t("contentAnalyticsPage.filters.compare")}
          </Typography>
        }
      />
    </Stack>
  );
}

function FocusBar({
  filters,
  labels,
  options,
  clear,
}: {
  filters: PlaybackFilters;
  labels: Record<string, string>;
  options: PlaybackFilterOptions | null;
  clear: (k: string) => void;
}) {
  const { t } = useTranslation("analytics");
  const chips: { key: string; text: string }[] = [];
  if (filters.asset) {
    chips.push({
      key: "asset",
      text: t("contentAnalyticsPage.focus.title", {
        name: labels[`asset:${filters.asset}`] ?? filters.asset,
      }),
    });
  }
  if (filters.provider) {
    const name =
      options?.providers.find((p) => p.id === filters.provider)?.label ??
      labels[`provider:${filters.provider}`] ??
      filters.provider;
    chips.push({
      key: "provider",
      text: t("contentAnalyticsPage.focus.provider", { name }),
    });
  }
  if (filters.collection) {
    const name =
      options?.collections.find((c) => c.id === filters.collection)?.label ??
      filters.collection;
    chips.push({
      key: "collection",
      text: t("contentAnalyticsPage.focus.collection", { name }),
    });
  }
  if (filters.premise) {
    const name =
      options?.premises.find((p) => p.key === filters.premise)?.label ??
      labels[`premise:${filters.premise}`] ??
      filters.premise;
    chips.push({
      key: "premise",
      text: t("contentAnalyticsPage.focus.premise", { name }),
    });
  }
  if (filters.device) {
    chips.push({
      key: "device",
      text: t("contentAnalyticsPage.focus.device", { name: filters.device }),
    });
  }
  if (chips.length === 0) return null;
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ mb: 2 }}
      data-testid="playbacks-focus"
    >
      <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
        {t("contentAnalyticsPage.focus.showingOnly")}
      </Typography>
      {chips.map((c) => (
        <Chip
          key={c.key}
          label={c.text}
          onDelete={() => clear(c.key)}
          color="primary"
          variant="outlined"
          size="small"
          data-testid={`playbacks-focus-${c.key}`}
        />
      ))}
    </Stack>
  );
}

function Footnote({ summary }: { summary: PlaybackSummary }) {
  const { t } = useTranslation("analytics");
  const pf = usePlaybackFormat();
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      component="p"
      sx={{ mt: 3 }}
      data-testid="playbacks-footnote"
    >
      {useSurface() === "owner"
        ? t("contentAnalyticsPage.footnote.owner")
        : t("contentAnalyticsPage.footnote.staff")}{" "}
      {summary.dataSince
        ? t("contentAnalyticsPage.footnote.collectedSince", {
            date: pf.day(summary.dataSince),
          })
        : t("contentAnalyticsPage.footnote.noData")}{" "}
      {t("contentAnalyticsPage.footnote.rest", {
        from: summary.previousRange.from,
        to: summary.previousRange.to,
      })}
    </Typography>
  );
}

/* ------------------------------------------------------------------------ */
/* Shared bits                                                               */
/* ------------------------------------------------------------------------ */

interface TabProps {
  filters: PlaybackFilters;
  compare: boolean;
  drill: (u: Record<string, string | null>, tab?: TabKey) => void;
  remember: (k: string, v: string) => void;
}

function Panel({
  title,
  subtitle,
  action,
  children,
  testId,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }} data-testid={testId}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography variant="subtitle1">{title}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function trendPoints(
  pf: PlaybackFormat,
  series: PlaybackSeriesPoint[],
  previous: PlaybackSeriesPoint[],
  granularity: "day" | "hour",
  pick: (p: PlaybackSeriesPoint) => number | null
) {
  return series.map((p, i) => ({
    label: pf.bucket(p.bucket, granularity),
    value: pick(p),
    previous: previous[i] ? pick(previous[i]) : null,
  }));
}

function useBreakdown(
  filters: PlaybackFilters,
  by: PlaybackDimension | null,
  remember: (k: string, v: string) => void
) {
  const surface = useSurface();
  const res = useLoad<PlaybackBreakdown | null>(
    () =>
      by
        ? foyerApi.playbackBreakdown(filters, by, surface)
        : Promise.resolve(null),
    `${JSON.stringify(filters)}|${by}|${surface}`
  );
  useEffect(() => {
    if (!res.data) return;
    if (by !== "asset" && by !== "provider" && by !== "premise") return;
    for (const r of res.data.rows.slice(0, 200)) {
      remember(`${by}:${r.key}`, r.label);
    }
  }, [res.data, by, remember]);
  return res;
}

function Loading({ error }: { error: string | null }) {
  return error ? (
    <Alert severity="error">{error}</Alert>
  ) : (
    <CircularProgress size={20} />
  );
}

function bars(
  rows: PlaybackBreakdownRow[],
  metric: keyof PlaybackMetrics,
  format: (v: number) => string,
  compare: boolean,
  withImages = false
) {
  return rows.map((r) => ({
    key: r.key,
    label: r.label,
    sublabel: r.sublabel,
    imageUrl: withImages ? (r.imageUrl ?? null) : undefined,
    value: r.metrics[metric] ?? 0,
    valueLabel: format(r.metrics[metric] ?? 0),
    previous: compare && metric === "plays" ? r.previousPlays : undefined,
  }));
}

function TrendPanel({
  title,
  subtitle,
  summary,
  compare,
  pick,
  format,
  toggle,
  testId,
}: {
  title: string;
  subtitle: string;
  summary: PlaybackSummary;
  compare: boolean;
  pick: (p: PlaybackSeriesPoint) => number | null;
  format: (v: number) => string;
  toggle: ReactNode;
  testId: string;
}) {
  const pf = usePlaybackFormat();
  return (
    <Panel title={title} subtitle={subtitle} action={toggle} testId={testId}>
      <TrendLine
        points={trendPoints(
          pf,
          summary.series,
          summary.previousSeries,
          summary.granularity,
          pick
        )}
        format={format}
        showPrevious={compare}
        ariaLabel={title}
      />
      <Box sx={{ mt: 1 }}>
        <TrendLegend
          current={`${summary.filters.from} – ${summary.filters.to}`}
          previous={
            compare
              ? `${summary.previousRange.from} – ${summary.previousRange.to}`
              : undefined
          }
        />
      </Box>
    </Panel>
  );
}

/* ------------------------------------------------------------------------ */
/* Overview                                                                  */
/* ------------------------------------------------------------------------ */

const TREND: Record<
  TrendMetric,
  {
    label: "plays" | "watchTime" | "uniqueUnits";
    pick: (p: PlaybackSeriesPoint) => number;
    format: keyof PlaybackFormat;
  }
> = {
  plays: { label: "plays", pick: (p) => p.plays, format: "count" },
  watchHours: {
    label: "watchTime",
    pick: (p) => p.watchHours,
    format: "hours",
  },
  uniqueRooms: {
    label: "uniqueUnits",
    pick: (p) => p.uniqueRooms,
    format: "count",
  },
};

function Overview({
  summary,
  filters,
  compare,
  drill,
  remember,
}: TabProps & { summary: PlaybackSummary }) {
  const { t } = useTranslation("analytics");
  const pf = usePlaybackFormat();
  const [metric, setMetric] = useState<TrendMetric>("plays");
  // Streaming (TV playback) or Signage (walls/screens). Owners: streaming.
  const [media, setMedia] = useState<"streaming" | "signage">("streaming");
  const m = summary.metrics;
  const p = summary.previous;
  const prev = (v: number | null) => (compare ? v : undefined);
  const assets = useBreakdown(filters, "asset", remember);
  const surface = useSurface();
  const owner = surface === "owner";
  // An owner never sees premise names (Sigge, 2026-10-06): no request at all.
  const premises = useBreakdown(filters, owner ? null : "premise", remember);
  const providers = useBreakdown(filters, "provider", remember);
  const kinds = useBreakdown(filters, "kind", remember);
  const heat = useLoad<PlaybackHeatmap>(
    () => foyerApi.playbackHeatmap(filters, surface),
    `${JSON.stringify(filters)}|${surface}`
  );

  const tiles: {
    key: string;
    label: string;
    value: string;
    now: number | null;
    before: number | null;
    hint?: string;
    trend?: TrendMetric;
  }[] = [
    {
      key: "plays",
      label: t("contentAnalyticsPage.trend.plays"),
      value: pf.count(m.plays),
      now: m.plays,
      before: p.plays,
      trend: "plays",
    },
    {
      key: "watch",
      label: t("contentAnalyticsPage.trend.watchTime"),
      value: pf.hours(m.watchHours),
      now: m.watchHours,
      before: p.watchHours,
      trend: "watchHours",
    },
    {
      key: "rooms",
      label: t("contentAnalyticsPage.trend.uniqueUnits"),
      value: pf.count(m.uniqueRooms),
      now: m.uniqueRooms,
      before: p.uniqueRooms,
      trend: "uniqueRooms",
    },
    {
      key: "premises",
      label: t("contentAnalyticsPage.tiles.activePremises"),
      value: pf.count(m.premises),
      now: m.premises,
      before: p.premises,
    },
    {
      key: "avg",
      label: t("contentAnalyticsPage.tiles.avgTimePerPlay"),
      value: pf.minutes(m.avgViewMinutes),
      now: m.avgViewMinutes,
      before: p.avgViewMinutes,
    },
    {
      key: "completion",
      label: t("contentAnalyticsPage.tiles.vodCompletion"),
      value: pf.percent(m.completionRate),
      now: m.completionRate,
      before: p.completionRate,
      hint: t("contentAnalyticsPage.tiles.vodCompletionHint"),
    },
  ];

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
        }}
      >
        {tiles.map((t) => (
          <KpiTile
            key={t.key}
            label={t.label}
            value={t.value}
            now={t.now}
            before={prev(t.before)}
            better="up"
            hint={t.hint}
            selected={t.trend === metric}
            onClick={t.trend ? () => setMetric(t.trend!) : undefined}
            testId={`playbacks-kpi-${t.key}`}
          />
        ))}
      </Box>

      <TrendPanel
        title={t("contentAnalyticsPage.trend.overTime", {
          metric: t(`contentAnalyticsPage.trend.${TREND[metric].label}`),
        })}
        subtitle={
          summary.granularity === "hour"
            ? t("contentAnalyticsPage.trend.perHour")
            : t("contentAnalyticsPage.trend.perDay")
        }
        summary={summary}
        compare={compare}
        pick={TREND[metric].pick}
        format={pf[TREND[metric].format] as (v: number) => string}
        testId="playbacks-trend"
        toggle={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={metric}
            onChange={(_, v: TrendMetric | null) => v && setMetric(v)}
          >
            <ToggleButton value="plays">
              {t("contentAnalyticsPage.trend.plays")}
            </ToggleButton>
            <ToggleButton value="watchHours">
              {t("contentAnalyticsPage.trend.watchTime")}
            </ToggleButton>
            <ToggleButton value="uniqueRooms">
              {t("contentAnalyticsPage.trend.units")}
            </ToggleButton>
          </ToggleButtonGroup>
        }
      />

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
        }}
      >
        <Panel
          title={
            media === "signage"
              ? t("contentAnalyticsPage.panels.mostShownSignage")
              : t("contentAnalyticsPage.panels.mostWatched")
          }
          subtitle={
            media === "signage"
              ? t("contentAnalyticsPage.panels.signageSubtitle")
              : t("contentAnalyticsPage.panels.byPlaysClickTitle")
          }
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              {!owner && (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={media}
                  onChange={(_, v: "streaming" | "signage" | null) =>
                    v && setMedia(v)
                  }
                  data-testid="playbacks-media"
                >
                  <ToggleButton value="streaming">
                    {t("contentAnalyticsPage.panels.streaming")}
                  </ToggleButton>
                  <ToggleButton value="signage">
                    {t("contentAnalyticsPage.panels.signage")}
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              {media === "streaming" && (
                <Button size="small" onClick={() => drill({}, "content")}>
                  {t("contentAnalyticsPage.panels.allContent")}
                </Button>
              )}
            </Stack>
          }
          testId="playbacks-top-content"
        >
          {media === "signage" ? (
            <SignageList filters={filters} compare={compare} />
          ) : assets.data ? (
            <BarList
              rows={bars(
                assets.data.rows.slice(0, 10),
                "plays",
                pf.count,
                compare,
                true
              )}
              onSelect={(key) => drill({ asset: key })}
              empty={t("contentAnalyticsPage.panels.noPlays")}
              testId="playbacks-top-content-list"
            />
          ) : (
            <Loading error={assets.error} />
          )}
        </Panel>
        {!owner && (
          <Panel
            title={t("contentAnalyticsPage.panels.topPremises")}
            subtitle={t("contentAnalyticsPage.panels.byPlaysClickPremise")}
            action={
              <Button size="small" onClick={() => drill({}, "premises")}>
                {t("contentAnalyticsPage.panels.allPremises")}
              </Button>
            }
            testId="playbacks-top-premises"
          >
            {premises.data ? (
              <BarList
                rows={bars(
                  premises.data.rows.slice(0, 10),
                  "plays",
                  pf.count,
                  compare
                )}
                onSelect={(key) => drill({ premise: key })}
                empty={t("contentAnalyticsPage.panels.noPlays")}
                testId="playbacks-top-premises-list"
              />
            ) : (
              <Loading error={premises.error} />
            )}
          </Panel>
        )}
        <Panel
          title={t("contentAnalyticsPage.panels.providers")}
          subtitle={t("contentAnalyticsPage.panels.byPlaysClickProvider")}
          action={
            <Button size="small" onClick={() => drill({}, "providers")}>
              {t("contentAnalyticsPage.panels.allProviders")}
            </Button>
          }
          testId="playbacks-top-providers"
        >
          {providers.data ? (
            <BarList
              rows={bars(
                providers.data.rows.slice(0, 8),
                "plays",
                pf.count,
                compare
              )}
              onSelect={(key) => key && drill({ provider: key }, "content")}
              empty={t("contentAnalyticsPage.panels.noPlays")}
              testId="playbacks-top-providers-list"
            />
          ) : (
            <Loading error={providers.error} />
          )}
        </Panel>
        <Panel
          title={t("contentAnalyticsPage.panels.liveAndVod")}
          subtitle={t("contentAnalyticsPage.panels.shareOfPlays")}
          testId="playbacks-kinds"
        >
          {kinds.data ? (
            <KindSplit rows={kinds.data.rows} />
          ) : (
            <Loading error={kinds.error} />
          )}
        </Panel>
      </Box>

      <Panel
        title={t("contentAnalyticsPage.panels.whenViewingStarts")}
        subtitle={t("contentAnalyticsPage.panels.heatmapSubtitle")}
        testId="playbacks-heatmap"
      >
        {heat.data ? (
          <Heatmap cells={heat.data.cells} />
        ) : (
          <Loading error={heat.error} />
        )}
      </Panel>
    </Stack>
  );
}

/** The signage half of "most watched": both players, side by side. */
function SignageList({
  filters,
  compare,
}: {
  filters: PlaybackFilters;
  compare: boolean;
}) {
  const { t } = useTranslation("analytics");
  const pf = usePlaybackFormat();
  const res = useLoad<SignageBreakdown>(
    () => foyerApi.playbackSignage(filters),
    `signage|${JSON.stringify(filters)}`
  );
  if (!res.data) return <Loading error={res.error} />;
  const d = res.data;
  const filterName = (k: string) =>
    (FILTER_KEYS as readonly string[]).includes(k)
      ? t(
          `contentAnalyticsPage.filterNames.${k as (typeof FILTER_KEYS)[number]}`
        )
      : k;
  return (
    <Box data-testid="playbacks-signage">
      {d.notApplied.length > 0 && (
        <Typography
          variant="meta"
          sx={{ color: tokens.inkMuted, mb: 1 }}
          data-testid="playbacks-signage-not-applied"
        >
          {t("contentAnalyticsPage.signageList.notApplied", {
            filters: d.notApplied.map(filterName).join(", "),
          })}
        </Typography>
      )}
      <BarList
        rows={d.rows.slice(0, 10).map((r) => ({
          key: `${r.source}:${r.key}`,
          label: r.label,
          sublabel: [
            r.type,
            r.source === "autoplayer"
              ? t("contentAnalyticsPage.signageList.autoplayer")
              : t("contentAnalyticsPage.signageList.foyerPlayer"),
            r.screenDays !== null
              ? t("contentAnalyticsPage.signageList.screenDays", {
                  count: r.screenDays,
                })
              : null,
            t("contentAnalyticsPage.signageList.premisesCount", {
              count: r.premises,
            }),
          ]
            .filter(Boolean)
            .join(SEPARATOR),
          value: r.plays,
          valueLabel: pf.count(r.plays),
          previous: compare ? r.previousPlays : undefined,
        }))}
        empty={
          d.dataSince
            ? t("contentAnalyticsPage.signageList.nothingShown")
            : t("contentAnalyticsPage.signageList.noData")
        }
        testId="playbacks-signage-list"
      />
      <Typography variant="micro" sx={{ color: tokens.inkFaint, mt: 1 }}>
        {t("contentAnalyticsPage.signageList.totals", {
          plays: pf.count(d.totals.plays),
          items: pf.count(d.totals.items),
          premises: pf.count(d.totals.premises),
        })}
      </Typography>
    </Box>
  );
}

/** The Ads tab: loads its own report, independent of the playback summary. */
function AdsTab({
  filters,
  compare,
}: {
  filters: PlaybackFilters;
  compare: boolean;
}) {
  // Bumped when a sync finishes, so the report reloads with the new names.
  const [namesVersion, setNamesVersion] = useState(0);
  const res = useLoad<AdDeliveryReport>(
    () => foyerApi.adDelivery(filters),
    `ads|${JSON.stringify(filters)}|${namesVersion}`
  );
  const catalogue = useVistarCatalogue(() => setNamesVersion((v) => v + 1));
  const bar = (
    <Box sx={{ mb: 2 }}>
      <VistarCatalogueBar
        status={catalogue.status}
        syncing={catalogue.syncing}
        error={catalogue.error}
        onSync={catalogue.sync}
      />
    </Box>
  );
  if (!res.data) {
    return (
      <>
        {bar}
        <Loading error={res.error} />
      </>
    );
  }
  const since = res.data.foyer.dataSince;
  const prevFrom = shiftDay(
    filters.from,
    -(
      (Date.parse(`${filters.to}T00:00:00Z`) -
        Date.parse(`${filters.from}T00:00:00Z`)) /
        86_400_000 +
      1
    )
  );
  return (
    <Box sx={{ opacity: res.loading ? 0.6 : 1, transition: "opacity .2s" }}>
      {res.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {res.error}
        </Alert>
      )}
      {bar}
      <AdDeliveryView
        report={res.data}
        // Same rule as the rest of the page: compare only with a period we
        // actually have data for.
        compare={compare && Boolean(since && prevFrom >= since)}
      />
    </Box>
  );
}

/** How often a running catalogue sync is checked on. */
const CATALOGUE_POLL_MS = 2000;

/**
 * The Vistar creative catalogue's status, and "Sync now". While a sync runs
 * (started here, by someone else, or by the daily schedule) the status is
 * polled; when it finishes, `onSynced` reloads the report so the new names
 * show without a page refresh.
 */
function useVistarCatalogue(onSynced: () => void) {
  const { t } = useTranslation("analytics");
  const [status, setStatus] = useState<VistarCatalogueStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const syncing = starting || Boolean(status?.running);

  useEffect(() => {
    let cancelled = false;
    foyerApi
      .vistarCatalogue()
      .then((s) => !cancelled && setStatus(s))
      .catch((err) => !cancelled && setError(loadErrorText(t, err)));
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Poll while a run is in progress; reload the report when it ends.
  const runningId = status?.running?.id ?? null;
  useEffect(() => {
    if (!runningId) return;
    let cancelled = false;
    const timer = setInterval(() => {
      foyerApi
        .vistarCatalogue()
        .then((s) => {
          if (cancelled) return;
          setStatus(s);
          if (!s.running) onSynced();
        })
        .catch(() => undefined); // a blip: the next poll tries again
    }, CATALOGUE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // `onSynced` is a fresh closure each render; the run id is the identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningId]);

  const sync = () => {
    setStarting(true);
    setError(null);
    foyerApi
      .syncVistarCatalogue()
      .then(({ sync: run }) =>
        setStatus((s) => (s ? { ...s, running: run } : s))
      )
      .catch((err) => setError(loadErrorText(t, err)))
      .finally(() => setStarting(false));
  };

  return { status, syncing, error, sync };
}

/** Plays and watch time split by live/VOD, as two labelled 100 % bars. */
function KindSplit({ rows }: { rows: PlaybackBreakdownRow[] }) {
  const { t } = useTranslation("analytics");
  const pf = usePlaybackFormat();
  const named = rows
    .filter((r) => r.key === "live" || r.key === "vod")
    // Fixed order: colour follows the entity, never its rank.
    .sort((a, b) => (a.key === "vod" ? -1 : 1) - (b.key === "vod" ? -1 : 1));
  const unknown = rows.find((r) => r.key === "unknown");
  const colour = (key: string) =>
    key === "vod" ? tokens.vizAttention : tokens.vizMoney;
  const total = (k: keyof PlaybackMetrics) =>
    named.reduce((n, r) => n + (r.metrics[k] ?? 0), 0);
  const line = (
    k: keyof PlaybackMetrics,
    title: string,
    f: (v: number) => string
  ) => {
    const sum = total(k);
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="meta" sx={{ color: tokens.inkMuted, mb: 0.5 }}>
          {title}
        </Typography>
        <Stack
          direction="row"
          sx={{
            height: 14,
            borderRadius: "4px",
            overflow: "hidden",
            gap: "2px",
          }}
        >
          {named.map((r) => {
            const v = r.metrics[k] ?? 0;
            return sum > 0 && v > 0 ? (
              <Box
                key={r.key}
                title={`${r.label}: ${f(v)}`}
                sx={{ flex: v, bgcolor: colour(r.key) }}
              />
            ) : null;
          })}
        </Stack>
        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 0.5 }}
          flexWrap="wrap"
          useFlexGap
        >
          {named.map((r) => (
            <Stack
              key={r.key}
              direction="row"
              spacing={0.75}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  bgcolor: colour(r.key),
                }}
              />
              <Typography variant="meta" sx={{ color: tokens.ink }}>
                {r.label} {f(r.metrics[k] ?? 0)}
                {sum > 0 ? ` (${pf.percent((r.metrics[k] ?? 0) / sum)})` : ""}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    );
  };
  if (named.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("contentAnalyticsPage.panels.noPlays")}
      </Typography>
    );
  }
  return (
    <Box>
      {line("plays", t("contentAnalyticsPage.columns.plays"), pf.count)}
      {line(
        "watchHours",
        t("contentAnalyticsPage.columns.watchTime"),
        pf.hours
      )}
      {unknown && unknown.metrics.watchHours > 0 && (
        <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
          {t("contentAnalyticsPage.kindSplit.unknownViewing", {
            hours: pf.hours(unknown.metrics.watchHours),
          })}
        </Typography>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------------ */
/* Content, providers, premises                                              */
/* ------------------------------------------------------------------------ */

function AssetLabel({ row }: { row: PlaybackBreakdownRow }) {
  const { t } = useTranslation("analytics");
  const kind =
    row.asset?.type === "channel"
      ? t("contentAnalyticsPage.assetLabel.liveChannel")
      : row.asset?.type
        ? t("contentAnalyticsPage.filters.vod")
        : null;
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box
        sx={{
          width: 28,
          height: 40,
          flexShrink: 0,
          borderRadius: 0.5,
          bgcolor: tokens.rowAlt,
          backgroundImage: row.imageUrl ? `url("${row.imageUrl}")` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="bodySm"
          sx={{ fontWeight: "fontWeightMedium", color: tokens.ink }}
        >
          {row.label}
        </Typography>
        <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
          {[
            kind,
            row.sublabel,
            row.asset && !row.asset.resolved
              ? t("contentAnalyticsPage.assetLabel.notInCatalogue")
              : null,
          ]
            .filter(Boolean)
            .join(SEPARATOR)}
        </Typography>
      </Box>
    </Stack>
  );
}

function ContentTab({ filters, compare, drill, remember }: TabProps) {
  const { t } = useTranslation("analytics");
  const pf = usePlaybackFormat();
  const COL = useColumns();
  const owner = useSurface() === "owner";
  const b = useBreakdown(filters, "asset", remember);
  if (!b.data) return <Loading error={b.error} />;
  return (
    <Panel
      title={t("contentAnalyticsPage.tabs.content")}
      subtitle={t("contentAnalyticsPage.contentTab.subtitle", {
        count: pf.count(b.data.totalRows),
      })}
      testId="playbacks-content"
    >
      <BreakdownTable
        rows={b.data.rows}
        firstColumn={t("contentAnalyticsPage.quality.dimensions.asset")}
        columns={[
          COL.plays,
          COL.rooms,
          COL.premises,
          COL.watch,
          COL.avg,
          COL.completion,
          ...(owner ? [] : [COL.startup]),
        ]}
        showChange={compare}
        onSelect={(r) => drill({ asset: r.key }, "overview")}
        searchPlaceholder={t("contentAnalyticsPage.contentTab.search")}
        empty={t("contentAnalyticsPage.panels.noPlays")}
        testId="playbacks-content-table"
        renderLabel={(r) => <AssetLabel row={r} />}
      />
    </Panel>
  );
}

function ProvidersTab({ filters, compare, drill, remember }: TabProps) {
  const { t } = useTranslation("analytics");
  const COL = useColumns();
  const b = useBreakdown(filters, "provider", remember);
  if (!b.data) return <Loading error={b.error} />;
  return (
    <Panel
      title={t("contentAnalyticsPage.tabs.providers")}
      subtitle={t("contentAnalyticsPage.providersTab.subtitle")}
      testId="playbacks-providers"
    >
      <BreakdownTable
        rows={b.data.rows}
        firstColumn={t("contentAnalyticsPage.filters.provider")}
        columns={[
          COL.plays,
          COL.rooms,
          COL.premises,
          COL.watch,
          COL.avg,
          COL.completion,
        ]}
        showChange={compare}
        onSelect={(r) => {
          if (r.key) drill({ provider: r.key }, "content");
        }}
        searchPlaceholder={t("contentAnalyticsPage.providersTab.search")}
        empty={t("contentAnalyticsPage.panels.noPlays")}
        testId="playbacks-providers-table"
      />
    </Panel>
  );
}

function PremisesTab({ filters, compare, drill, remember }: TabProps) {
  const { t } = useTranslation("analytics");
  const pf = usePlaybackFormat();
  const COL = useColumns();
  const b = useBreakdown(filters, "premise", remember);
  if (!b.data) return <Loading error={b.error} />;
  return (
    <Panel
      title={t("contentAnalyticsPage.tabs.premises")}
      subtitle={t(
        filters.collection
          ? "contentAnalyticsPage.premisesTab.subtitleCollection"
          : "contentAnalyticsPage.premisesTab.subtitle",
        { count: pf.count(b.data.totalRows) }
      )}
      action={
        <Button
          size="small"
          variant="outlined"
          component="a"
          href={foyerApi.playbackBreakdownCsvUrl(filters, "premiseAsset")}
          startIcon={<DownloadOutlinedIcon />}
          data-testid="playbacks-premise-asset-export"
        >
          {t("contentAnalyticsPage.premisesTab.premiseTitleCsv")}
        </Button>
      }
      testId="playbacks-premises"
    >
      <BreakdownTable
        rows={b.data.rows}
        firstColumn={t("contentAnalyticsPage.filters.premise")}
        columns={[
          COL.plays,
          COL.rooms,
          COL.watch,
          COL.avg,
          COL.startup,
          COL.playFail,
          COL.rebuffer,
        ]}
        showChange={compare}
        onSelect={(r) =>
          drill({ premise: r.key }, filters.collection ? "content" : "overview")
        }
        searchPlaceholder={t("contentAnalyticsPage.premisesTab.search")}
        empty={t("contentAnalyticsPage.panels.noPlays")}
        testId="playbacks-premises-table"
      />
    </Panel>
  );
}

/* ------------------------------------------------------------------------ */
/* Quality                                                                   */
/* ------------------------------------------------------------------------ */

const QUALITY_DIMENSIONS = [
  "device",
  "os",
  "premise",
  "asset",
  "error",
] as const satisfies readonly PlaybackDimension[];

const QUALITY_TREND: Record<
  QualityMetric,
  {
    label: "startupMedian" | "buffering" | "failures";
    pick: (p: PlaybackSeriesPoint) => number | null;
    format: keyof PlaybackFormat;
  }
> = {
  startupMedianMs: {
    label: "startupMedian",
    pick: (p) => p.startupMedianMs,
    format: "ms",
  },
  rebufferRatio: {
    label: "buffering",
    pick: (p) => p.rebufferRatio,
    format: "percent",
  },
  failureRate: {
    label: "failures",
    pick: (p) => p.failureRate,
    format: "percent",
  },
};

function QualityTab({
  summary,
  filters,
  compare,
  drill,
  remember,
}: TabProps & { summary: PlaybackSummary }) {
  const { t } = useTranslation("analytics");
  const pf = usePlaybackFormat();
  const COL = useColumns();
  const qualityStatus = useQualityStatus();
  const [metric, setMetric] = useState<QualityMetric>("startupMedianMs");
  const [dim, setDim] = useState<(typeof QUALITY_DIMENSIONS)[number]>("device");
  const b = useBreakdown(filters, dim, remember);
  const m = summary.metrics;
  const p = summary.previous;
  const prev = (v: number | null) => (compare ? v : undefined);

  const tiles: {
    key: string;
    label: string;
    value: string;
    now: number | null;
    before: number | null;
    better: Better;
    status?: { level: Level; text: string };
    hint?: string;
    trend?: QualityMetric;
  }[] = [
    {
      key: "startup",
      label: t("contentAnalyticsPage.quality.startupMedian"),
      value: pf.ms(m.startupMedianMs),
      now: m.startupMedianMs,
      before: p.startupMedianMs,
      better: "down",
      status: qualityStatus("startupMedianMs", m.startupMedianMs),
      hint: t("contentAnalyticsPage.quality.p90Hint", {
        value: pf.ms(m.startupP90Ms),
      }),
      trend: "startupMedianMs",
    },
    {
      key: "rebuffer",
      label: t("contentAnalyticsPage.quality.buffering"),
      value: pf.percent(m.rebufferRatio),
      now: m.rebufferRatio,
      before: p.rebufferRatio,
      better: "down",
      status: qualityStatus("rebufferRatio", m.rebufferRatio),
      hint: t("contentAnalyticsPage.quality.stallsHint", {
        value: pf.perHour(m.rebuffersPerHour),
      }),
      trend: "rebufferRatio",
    },
    {
      key: "playfail",
      label: t("contentAnalyticsPage.quality.failedDuringPlay"),
      value: pf.percent(m.playbackFailureRate),
      now: m.playbackFailureRate,
      before: p.playbackFailureRate,
      better: "down",
      status: qualityStatus("playbackFailureRate", m.playbackFailureRate),
      trend: "failureRate",
    },
    {
      key: "startfail",
      label: t("contentAnalyticsPage.quality.failedToStart"),
      value: pf.percent(m.startFailureRate),
      now: m.startFailureRate,
      before: p.startFailureRate,
      better: "down",
      status: qualityStatus("startFailureRate", m.startFailureRate),
    },
    {
      key: "ebvs",
      label: t("contentAnalyticsPage.quality.gaveUpBeforePicture"),
      value: pf.percent(m.ebvsRate),
      now: m.ebvsRate,
      before: p.ebvsRate,
      better: "down",
      status: qualityStatus("ebvsRate", m.ebvsRate),
      hint: t("contentAnalyticsPage.quality.gaveUpHint"),
    },
    {
      key: "zap",
      label: t("contentAnalyticsPage.quality.zapped"),
      value: pf.percent(m.zapRate),
      now: m.zapRate,
      before: p.zapRate,
      better: "none",
    },
    {
      key: "bitrate",
      label: t("contentAnalyticsPage.quality.avgBitrate"),
      value: pf.kbps(m.avgBitrateKbps),
      now: m.avgBitrateKbps,
      before: p.avgBitrateKbps,
      better: "none",
    },
  ];

  const onSelect =
    dim === "device"
      ? (r: PlaybackBreakdownRow) =>
          drill({ device: r.key === "unknown" ? null : r.key })
      : dim === "premise"
        ? (r: PlaybackBreakdownRow) => drill({ premise: r.key })
        : dim === "asset"
          ? (r: PlaybackBreakdownRow) => drill({ asset: r.key })
          : undefined;

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
        }}
      >
        {tiles.map((t) => (
          <KpiTile
            key={t.key}
            label={t.label}
            value={t.value}
            now={t.now}
            before={prev(t.before)}
            better={t.better}
            status={t.status}
            hint={t.hint}
            selected={t.trend === metric}
            onClick={t.trend ? () => setMetric(t.trend!) : undefined}
            testId={`playbacks-quality-${t.key}`}
          />
        ))}
      </Box>

      <TrendPanel
        title={t("contentAnalyticsPage.trend.overTime", {
          metric: t(
            `contentAnalyticsPage.quality.${QUALITY_TREND[metric].label}`
          ),
        })}
        subtitle={t("contentAnalyticsPage.quality.thresholds")}
        summary={summary}
        compare={compare}
        pick={QUALITY_TREND[metric].pick}
        format={pf[QUALITY_TREND[metric].format] as (v: number) => string}
        testId="playbacks-quality-trend"
        toggle={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={metric}
            onChange={(_, v: QualityMetric | null) => v && setMetric(v)}
          >
            <ToggleButton value="startupMedianMs">
              {t("contentAnalyticsPage.quality.startup")}
            </ToggleButton>
            <ToggleButton value="rebufferRatio">
              {t("contentAnalyticsPage.quality.buffering")}
            </ToggleButton>
            <ToggleButton value="failureRate">
              {t("contentAnalyticsPage.quality.failures")}
            </ToggleButton>
          </ToggleButtonGroup>
        }
      />

      <Panel
        title={t("contentAnalyticsPage.quality.wherePlays")}
        subtitle={t("contentAnalyticsPage.quality.byDimension")}
        action={
          <TextField
            select
            size="small"
            label={t("contentAnalyticsPage.quality.by")}
            value={dim}
            onChange={(e) =>
              setDim(e.target.value as (typeof QUALITY_DIMENSIONS)[number])
            }
            sx={{ minWidth: 170 }}
            data-testid="playbacks-quality-dimension"
          >
            {QUALITY_DIMENSIONS.map((d) => (
              <MenuItem key={d} value={d}>
                {t(`contentAnalyticsPage.quality.dimensions.${d}`)}
              </MenuItem>
            ))}
          </TextField>
        }
        testId="playbacks-quality-breakdown"
      >
        {b.data ? (
          <BreakdownTable
            rows={b.data.rows}
            firstColumn={t(`contentAnalyticsPage.quality.dimensions.${dim}`)}
            columns={QUALITY_COLUMNS.map((c) => COL[c])}
            showChange={false}
            onSelect={onSelect}
            searchPlaceholder={t("contentAnalyticsPage.quality.search")}
            empty={
              dim === "error"
                ? t("contentAnalyticsPage.quality.noErrors")
                : t("contentAnalyticsPage.quality.noData")
            }
            testId="playbacks-quality-table"
            renderLabel={
              dim === "asset" ? (r) => <AssetLabel row={r} /> : undefined
            }
          />
        ) : (
          <Loading error={b.error} />
        )}
      </Panel>
    </Stack>
  );
}
